'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  Clock, 
  AlertTriangle, 
  Filter, 
  Search,
  Calendar,
  ChevronDown,
  Download,
  Eye
} from 'lucide-react'
import { formatCurrency, checkSEPRELADThreshold } from '../utils/currency'
import { BirhausCurrencyDisplay } from './BirhausCurrencyDisplay'
import type { 
  BirhausTransactionListProps, 
  Transaction, 
  TransactionType,
  TransactionStatus,
  TransactionFilters
} from '../types/financial'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Transaction type configuration
const transactionTypeConfig: Record<TransactionType, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  ingreso: {
    icon: <ArrowDownLeft size={16} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Ingreso',
    nameEn: 'Income'
  },
  gasto: {
    icon: <ArrowUpRight size={16} />,
    colorClass: 'text-red-600 bg-red-100',
    nameEs: 'Gasto',
    nameEn: 'Expense'
  },
  transferencia: {
    icon: <Clock size={16} />,
    colorClass: 'text-blue-600 bg-blue-100',
    nameEs: 'Transferencia',
    nameEn: 'Transfer'
  },
  inversion: {
    icon: <ArrowDownLeft size={16} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Inversión',
    nameEn: 'Investment'
  }
}

// Transaction status configuration
const transactionStatusConfig: Record<TransactionStatus, {
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  pendiente: {
    colorClass: 'text-yellow-700 bg-yellow-100',
    nameEs: 'Pendiente',
    nameEn: 'Pending'
  },
  completada: {
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'Completada',
    nameEn: 'Completed'
  },
  fallida: {
    colorClass: 'text-red-700 bg-red-100',
    nameEs: 'Fallida',
    nameEn: 'Failed'
  },
  cancelada: {
    colorClass: 'text-gray-700 bg-gray-100',
    nameEs: 'Cancelada',
    nameEn: 'Cancelled'
  },
  procesando: {
    colorClass: 'text-blue-700 bg-blue-100',
    nameEs: 'Procesando',
    nameEn: 'Processing'
  }
}

/**
 * BirhausTransactionList - Transaction history display component
 * 
 * Features:
 * - Comprehensive transaction filtering and search
 * - SEPRELAD compliance flagging for large transactions
 * - Spanish-first transaction type and status names
 * - Miller's Law compliance (max 7 visible transactions per page)
 * - Responsive layout for different screen sizes
 * - Export functionality for transaction reports
 * - Real-time status updates and error handling
 * - Accessibility compliant with proper ARIA labels
 */
export function BirhausTransactionList({
  transactions,
  loading = false,
  pageSize = 7,
  showFilters = true,
  showSearch = true,
  showExport = true,
  onTransactionClick,
  onExport,
  className,
  ...props
}: BirhausTransactionListProps) {
  const { language, reportViolation } = useBirhaus()
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = React.useState('')
  const [filters, setFilters] = React.useState<TransactionFilters>({})
  const [currentPage, setCurrentPage] = React.useState(1)
  const [showFiltersPanel, setShowFiltersPanel] = React.useState(false)

  // BIRHAUS validation: Check cognitive load (max 7 transactions - Miller's Law)
  React.useEffect(() => {
    if (pageSize > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Transaction list shows ${pageSize} items per page (max recommended: 7)`,
        messageEs: `Lista de transacciones muestra ${pageSize} elementos por página (máximo recomendado: 7)`,
        messageEn: `Transaction list shows ${pageSize} items per page (max recommended: 7)`,
        recommendation: 'Use pagination to keep cognitive load manageable',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [pageSize, reportViolation])

  // Filter transactions based on search and filters
  const filteredTransactions = React.useMemo(() => {
    let filtered = transactions

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(transaction =>
        transaction.descripcionEs.toLowerCase().includes(term) ||
        transaction.referencia?.toLowerCase().includes(term) ||
        transaction.cuentaDestino?.toLowerCase().includes(term) ||
        transaction.cuentaOrigen?.toLowerCase().includes(term)
      )
    }

    // Type filter
    if (filters.tipo) {
      filtered = filtered.filter(t => filters.tipo!.includes(t.tipo))
    }

    // Status filter
    if (filters.estado) {
      filtered = filtered.filter(t => filters.estado!.includes(t.estado))
    }

    // Date range filter
    if (filters.fechaDesde) {
      filtered = filtered.filter(t => new Date(t.fecha) >= new Date(filters.fechaDesde!))
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(t => new Date(t.fecha) <= new Date(filters.fechaHasta!))
    }

    // Amount range filter
    if (filters.montoMinimo !== undefined) {
      filtered = filtered.filter(t => t.monto >= filters.montoMinimo!)
    }
    if (filters.montoMaximo !== undefined) {
      filtered = filtered.filter(t => t.monto <= filters.montoMaximo!)
    }

    // Currency filter
    if (filters.moneda) {
      filtered = filtered.filter(t => filters.moneda!.includes(t.moneda))
    }

    return filtered.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
  }, [transactions, searchTerm, filters])

  // Paginate results
  const totalPages = Math.ceil(filteredTransactions.length / pageSize)
  const paginatedTransactions = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredTransactions.slice(start, start + pageSize)
  }, [filteredTransactions, currentPage, pageSize])

  // Handle search
  const handleSearch = React.useCallback((value: string) => {
    setSearchTerm(value)
    setCurrentPage(1) // Reset to first page
  }, [])

  // Handle filter changes
  const handleFilterChange = React.useCallback((newFilters: Partial<TransactionFilters>) => {
    setFilters((prev: TransactionFilters) => ({ ...prev, ...newFilters }))
    setCurrentPage(1) // Reset to first page
  }, [])

  // Clear filters
  const clearFilters = React.useCallback(() => {
    setFilters({})
    setSearchTerm('')
    setCurrentPage(1)
  }, [])

  // Handle transaction click
  const handleTransactionClick = React.useCallback((transaction: Transaction) => {
    onTransactionClick?.(transaction)
  }, [onTransactionClick])

  // Handle export
  const handleExport = React.useCallback(() => {
    onExport?.(filteredTransactions)
  }, [onExport, filteredTransactions])

  // Check if transaction requires SEPRELAD reporting
  const checkSEPRELADCompliance = React.useCallback((transaction: Transaction) => {
    const seprelad = checkSEPRELADThreshold(transaction.monto, transaction.moneda)
    return seprelad.requiresReport
  }, [])

  if (loading) {
    return (
      <div className={cn('space-y-4', className)} {...props}>
        {/* Loading skeleton */}
        {Array.from({ length: pageSize }).map((_, index) => (
          <div key={index} className="p-4 border border-birhaus-border rounded-lg">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
                <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3" />
              </div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)} {...props}>
      {/* Header with search and filters */}
      {(showSearch || showFilters || showExport) && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-1">
            {/* Search */}
            {showSearch && (
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-birhaus-muted-foreground" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={language === 'es' ? 'Buscar transacciones...' : 'Search transactions...'}
                  className="w-full pl-10 pr-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:border-birhaus-primary"
                />
              </div>
            )}

            {/* Filter toggle */}
            {showFilters && (
              <button
                onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                className="flex items-center gap-2 px-4 py-2 border border-birhaus-border rounded-lg hover:bg-birhaus-muted/50 transition-colors"
              >
                <Filter size={16} />
                <span>{language === 'es' ? 'Filtros' : 'Filters'}</span>
                <ChevronDown size={16} className={cn(
                  'transition-transform duration-200',
                  showFiltersPanel && 'rotate-180'
                )} />
              </button>
            )}
          </div>

          {/* Export button */}
          {showExport && (
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-birhaus-primary text-white rounded-lg hover:bg-birhaus-primary/90 transition-colors"
            >
              <Download size={16} />
              <span>{language === 'es' ? 'Exportar' : 'Export'}</span>
            </button>
          )}
        </div>
      )}

      {/* Filters panel */}
      {showFilters && showFiltersPanel && (
        <div className="p-4 border border-birhaus-border rounded-lg bg-birhaus-muted/10 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Transaction type filter */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'es' ? 'Tipo de Transacción' : 'Transaction Type'}
              </label>
              <select
                value={filters.tipo?.join(',') || ''}
                onChange={(e) => handleFilterChange({
                  tipo: e.target.value ? e.target.value.split(',') as TransactionType[] : undefined
                })}
                className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
              >
                <option value="">{language === 'es' ? 'Todos' : 'All'}</option>
                {Object.entries(transactionTypeConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {language === 'es' ? config.nameEs : config.nameEn}
                  </option>
                ))}
              </select>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'es' ? 'Estado' : 'Status'}
              </label>
              <select
                value={filters.estado?.join(',') || ''}
                onChange={(e) => handleFilterChange({
                  estado: e.target.value ? e.target.value.split(',') as TransactionStatus[] : undefined
                })}
                className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
              >
                <option value="">{language === 'es' ? 'Todos' : 'All'}</option>
                {Object.entries(transactionStatusConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {language === 'es' ? config.nameEs : config.nameEn}
                  </option>
                ))}
              </select>
            </div>

            {/* Date range */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'es' ? 'Fecha Desde' : 'Date From'}
              </label>
              <input
                type="date"
                value={filters.fechaDesde instanceof Date ? filters.fechaDesde.toISOString().split('T')[0] : filters.fechaDesde || ''}
                onChange={(e) => handleFilterChange({ fechaDesde: e.target.value || undefined })}
                className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'es' ? 'Fecha Hasta' : 'Date To'}
              </label>
              <input
                type="date"
                value={filters.fechaHasta instanceof Date ? filters.fechaHasta.toISOString().split('T')[0] : filters.fechaHasta || ''}
                onChange={(e) => handleFilterChange({ fechaHasta: e.target.value || undefined })}
                className="w-full p-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
              />
            </div>
          </div>

          {/* Clear filters */}
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-birhaus-muted-foreground hover:text-birhaus-foreground transition-colors"
            >
              {language === 'es' ? 'Limpiar Filtros' : 'Clear Filters'}
            </button>
          </div>
        </div>
      )}

      {/* Results summary */}
      <div className="flex items-center justify-between text-sm text-birhaus-muted-foreground">
        <span>
          {language === 'es' 
            ? `Mostrando ${paginatedTransactions.length} de ${filteredTransactions.length} transacciones`
            : `Showing ${paginatedTransactions.length} of ${filteredTransactions.length} transactions`
          }
        </span>
        {filteredTransactions.length !== transactions.length && (
          <span>
            ({language === 'es' ? 'filtrado de' : 'filtered from'} {transactions.length})
          </span>
        )}
      </div>

      {/* Transaction list */}
      <div className="space-y-3">
        {paginatedTransactions.length === 0 ? (
          <div className="p-8 text-center text-birhaus-muted-foreground">
            <Clock size={48} className="mx-auto mb-4 text-birhaus-muted-foreground/50" />
            <p>
              {language === 'es' 
                ? 'No se encontraron transacciones'
                : 'No transactions found'}
            </p>
            {(searchTerm || Object.keys(filters).length > 0) && (
              <button
                onClick={clearFilters}
                className="mt-2 text-birhaus-primary hover:underline"
              >
                {language === 'es' ? 'Limpiar filtros' : 'Clear filters'}
              </button>
            )}
          </div>
        ) : (
          paginatedTransactions.map((transaction) => {
            const typeConfig = transactionTypeConfig[transaction.tipo]
            const statusConfig = transactionStatusConfig[transaction.estado]
            const requiresSEPRELAD = checkSEPRELADCompliance(transaction)
            const isClickable = Boolean(onTransactionClick)
            const isCredit = transaction.tipo === 'ingreso' || transaction.tipo === 'inversion'

            return (
              <div
                key={transaction.id}
                className={cn(
                  'p-4 border border-birhaus-border rounded-lg transition-all duration-200',
                  isClickable && 'cursor-pointer hover:border-birhaus-primary/50 hover:shadow-sm',
                  'focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:border-birhaus-primary'
                )}
                onClick={() => isClickable && handleTransactionClick(transaction)}
                onKeyDown={(e) => {
                  if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    handleTransactionClick(transaction)
                  }
                }}
                tabIndex={isClickable ? 0 : -1}
                role={isClickable ? 'button' : undefined}
                aria-label={`${transaction.descripcionEs}, ${formatCurrency(transaction.monto, { currency: transaction.moneda })}, ${transaction.fecha.toLocaleDateString()}`}
              >
                <div className="flex items-center gap-4">
                  {/* Transaction type icon */}
                  <div className={cn(
                    'p-2 rounded-full flex-shrink-0 relative',
                    typeConfig.colorClass
                  )}>
                    {typeConfig.icon}
                    {/* SEPRELAD warning indicator */}
                    {requiresSEPRELAD && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white">
                        <AlertTriangle size={8} className="text-white" />
                      </div>
                    )}
                  </div>

                  {/* Transaction details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-birhaus-foreground truncate">
                        {transaction.descripcionEs}
                      </h3>
                      
                      {/* Transaction status */}
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full flex-shrink-0',
                        statusConfig.colorClass
                      )}>
                        {language === 'es' ? statusConfig.nameEs : statusConfig.nameEn}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="space-y-1 min-w-0">
                        <p className="text-sm text-birhaus-muted-foreground truncate">
                          {language === 'es' ? typeConfig.nameEs : typeConfig.nameEn}
                          {transaction.referencia && ` • ${transaction.referencia}`}
                        </p>
                        
                        {/* Origin/Destination info */}
                        {(transaction.cuentaOrigen || transaction.cuentaDestino) && (
                          <p className="text-xs text-birhaus-muted-foreground truncate">
                            {isCredit ? (
                              <>
                                {language === 'es' ? 'De: ' : 'From: '}
                                {transaction.cuentaOrigen}
                              </>
                            ) : (
                              <>
                                {language === 'es' ? 'Para: ' : 'To: '}
                                {transaction.cuentaDestino}
                              </>
                            )}
                          </p>
                        )}

                        {/* Transaction date and time */}
                        <p className="text-xs text-birhaus-muted-foreground">
                          {transaction.fecha.toLocaleDateString(language === 'es' ? 'es-PY' : 'en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>

                      {/* Amount display */}
                      <div className="text-right flex-shrink-0">
                        <p className={cn(
                          'font-semibold',
                          isCredit ? 'text-green-600' : 'text-red-600'
                        )}>
                          {isCredit ? '+' : '-'}
                          <BirhausCurrencyDisplay 
                            amount={Math.abs(transaction.monto)} 
                            currency={transaction.moneda}
                            className={isCredit ? 'text-green-600' : 'text-red-600'}
                          />
                        </p>
                        
                        {/* Commission info */}
                        {transaction.comision && transaction.comision > 0 && (
                          <p className="text-xs text-birhaus-muted-foreground">
                            {language === 'es' ? 'Comisión: ' : 'Fee: '}
                            <BirhausCurrencyDisplay 
                              amount={transaction.comision} 
                              currency={transaction.moneda}
                            />
                          </p>
                        )}

                        {/* SEPRELAD warning */}
                        {requiresSEPRELAD && (
                          <p className="text-xs text-yellow-600 font-medium">
                            {language === 'es' ? 'SEPRELAD' : 'SEPRELAD'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* View details indicator */}
                  {isClickable && (
                    <div className="flex-shrink-0 text-birhaus-muted-foreground">
                      <Eye size={16} />
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm border border-birhaus-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-birhaus-muted/50 transition-colors"
          >
            {language === 'es' ? 'Anterior' : 'Previous'}
          </button>

          <span className="text-sm text-birhaus-muted-foreground">
            {language === 'es' 
              ? `Página ${currentPage} de ${totalPages}`
              : `Page ${currentPage} of ${totalPages}`
            }
          </span>

          <button
            onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm border border-birhaus-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-birhaus-muted/50 transition-colors"
          >
            {language === 'es' ? 'Siguiente' : 'Next'}
          </button>
        </div>
      )}
    </div>
  )
}

export type { BirhausTransactionListProps }